import type {
  BaseRecord,
  CrudFilters,
  DataProvider,
  GetListParams,
  GetOneParams,
} from '@refinedev/core'

import { productCatalogGetProduct, productCatalogListProducts } from '@repo/sdk'

import { getStoredUser } from '../lib/auth-storage'
import { i18n } from '../shared/i18n'

import { handleSdkError } from './sdkErrorUtils'
import { getApiUrl } from '../lib/runtime-env'

function extractStatus(
  filters?: CrudFilters,
): Array<'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SOLD_OUT'> | undefined {
  if (!filters) return undefined
  for (const filter of filters) {
    if ('field' in filter && filter.field === 'status' && 'value' in filter) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- filter value is status string
      const value = filter.value as string
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- filter value is validated status string
      if (value) return [value] as Array<'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SOLD_OUT'>
    }
  }
  return undefined
}

function extractName(filters?: CrudFilters): string | undefined {
  if (!filters) return undefined
  for (const filter of filters) {
    if ('field' in filter && filter.field === 'name' && 'value' in filter) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- filter value is name string
      const value = filter.value as string
      return value || undefined
    }
  }
  return undefined
}

function notImplemented(): never {
  throw new Error(i18n.t('common.errors.notImplemented'))
}

export const productListDataProvider: DataProvider = {
  getList: async <TData extends BaseRecord = BaseRecord>(params: GetListParams) => {
    try {
      const dealerId = getStoredUser()?.userId
      // Falling back to '' produced a confusing 400 from the API — fail with a message
      // the dealer can act on instead.
      if (!dealerId) throw new Error(i18n.t('common.errors.dealerIdRequired'))
      const page = params.pagination?.currentPage ?? 1
      const pageSize = params.pagination?.pageSize ?? 20
      const status = extractStatus(params.filters)
      const name = extractName(params.filters)

      const response = await productCatalogListProducts({
        query: { dealerId, page, pageSize, status, name },
      })

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SDK response matches TData structure
        data: (response.data?.data ?? []) as unknown as TData[],
        total: response.data?.meta.totalItems ?? 0,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(params: GetOneParams) => {
    try {
      const id = String(params.id)
      const response = await productCatalogGetProduct({
        path: { productId: id },
      })

      if (!response.data) {
        throw new Error(i18n.t('common.errors.detailNotFound'))
      }

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SDK response matches TData structure
        data: response.data as unknown as TData,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },

  create: notImplemented,
  update: notImplemented,
  deleteOne: notImplemented,
  getApiUrl,
}
